import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Search } from "lucide-react";

const NAV = [
  { to: "/states", label: "State Explorer" },
  { to: "/international", label: "International" },
  { to: "/claims", label: "Claims vs. Evidence" },
  { to: "/water-cooling", label: "Water & Cooling" },
  { to: "/newsroom", label: "Newsroom" },
  { to: "/methodology", label: "Methodology" },
];

export const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-0.5 font-display font-black text-xl tracking-tight">
    <span className="text-white">DYOR</span><span className="text-[#84CC16]">USA</span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <header className="glass sticky top-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#84CC16]" : "text-slate-300 hover:text-white"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="header-search-btn" onClick={() => navigate("/states")}
            className="hidden sm:flex items-center gap-2 text-slate-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 text-sm transition-colors hover:border-blue-500/50">
            <Search size={15} /> Explore
          </button>
          <Link to="/newsletter" data-testid="header-newsletter-btn"
            className="hidden sm:inline-flex bg-[#2563EB] hover:bg-[#3B82F6] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors glow-blue">
            Free Newsletter
          </Link>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0B0F17] px-4 py-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800 active:bg-slate-800">
              {n.label}
            </NavLink>
          ))}
          <NavLink to="/advertise" onClick={() => setOpen(false)}
            className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800 active:bg-slate-800">
            Advertise
          </NavLink>
          <NavLink to="/power-study" onClick={() => setOpen(false)} data-testid="mobile-nav-power-study"
            className="block px-3 py-3 rounded-lg text-[#84CC16] hover:bg-slate-800 active:bg-slate-800">
            Power Feasibility Study
          </NavLink>
          <NavLink to="/account" onClick={() => setOpen(false)} data-testid="mobile-nav-account"
            className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800 active:bg-slate-800">
            Customer login
          </NavLink>
          <Link to="/newsletter" onClick={() => setOpen(false)}
            className="block px-3 py-3 rounded-lg bg-[#2563EB] text-white font-semibold text-center mt-2">Free Newsletter</Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-white/10 bg-[#0B0F17] mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
      <div className="col-span-2 md:col-span-1">
        <Logo />
        <p className="text-slate-400 mt-3 leading-relaxed text-[13px]">Do Your Own Research. Independent public education about data centers and the claims made about them.</p>
      </div>
      <div>
        <p className="eyebrow mb-3">Explore</p>
        <ul className="space-y-2 text-slate-300">
          <li><Link to="/states" className="hover:text-white">State Explorer</Link></li>
          <li><Link to="/international" className="hover:text-white">International</Link></li>
          <li><Link to="/water-cooling" className="hover:text-white">Water & Cooling</Link></li>
          <li><Link to="/newsroom" className="hover:text-white">Newsroom</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-3">Trust</p>
        <ul className="space-y-2 text-slate-300">
          <li><Link to="/claims" className="hover:text-white">Claims vs. Evidence</Link></li>
          <li><Link to="/methodology" className="hover:text-white">Methodology & Sources</Link></li>
          <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-3">Business</p>
        <ul className="space-y-2 text-slate-300">
          <li><Link to="/advertise" className="hover:text-white">Advertise with DYORUSA</Link></li>
          <li><Link to="/power-study" data-testid="footer-power-study" className="hover:text-white">Power Feasibility Study</Link></li>
          <li><Link to="/account" data-testid="footer-account" className="hover:text-white">Customer login</Link></li>
          <li><Link to="/newsletter" className="hover:text-white">Weekly Newsletter</Link></li>
          <li><Link to="/admin" className="hover:text-white">Admin</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500 space-y-1">
      <p>12 Stone Development Group LLC · Thomas Reinke, Principal · <a href="tel:+19127238577" className="hover:text-slate-300">912-723-8577</a></p>
      <p>© {new Date().getFullYear()} DYORUSA · Advertising is clearly labeled and never affects research findings or editorial conclusions.</p>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
